import styles from "./EstimateLayoutStep6.module.scss";
import classNames from "classnames/bind";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import FormInpuEditor from "~/components/common/form/formInpuEditor/FormInpuEditor";
import type { EstimateLayoutStep6FormData as Step6FormData } from "~/utils/interface/estimateSetting";
import { IEstimateTemplateIntroduction } from "~/utils/interface/estimateSetting";
import { useState, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetEstimateTemplateIntroduction } from "~/thunks/estimate-setting/estimateSettingThunk";

const cx = classNames.bind(styles);

interface EstimateLayoutStep6Props {
    onStep6DataChange?: (data: Step6FormData) => void;
    initialData?: Step6FormData | null;
}

const EstimateLayoutStep6 = ({ onStep6DataChange, initialData }: EstimateLayoutStep6Props) => {
    const dispatch = useAppDispatch();
    const { estimateTemplateIntroduction } = useAppSelector((state) => state.estimateSetting);

    const DEFAULT_TITLE_STEP = "Step 6: Terms & Condition";
    const [formData, setFormData] = useState<Step6FormData | undefined>(() => ({
        ...initialData,
        titleStep6: initialData?.titleStep6 ?? DEFAULT_TITLE_STEP,
    }));

    const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const templateDropdownRef = useRef<HTMLDivElement>(null);
    const formDataRef = useRef(formData);

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target as Node)) {
                setTemplateDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleUpdateData = (update: Partial<Step6FormData>) => {
        const data: Step6FormData = { ...(formDataRef.current ?? {}), ...update } as Step6FormData;
        formDataRef.current = data;
        setFormData(data);
        onStep6DataChange?.(data);
    };

    const handleUseTemplateClick = () => {
        const nextOpen = !templateDropdownOpen;
        setTemplateDropdownOpen(nextOpen);
        if (nextOpen) {
            setTemplatesLoading(true);
            dispatch(handleGetEstimateTemplateIntroduction()).finally(() => {
                setTemplatesLoading(false);
            });
        }
    };

    const handleSelectTemplate = (template: IEstimateTemplateIntroduction) => {
        handleUpdateData({
            titleStep6: template.estimateTemplate.templateName ?? "",
            termsNConditions: template.estimateTemplate.templateDescription ?? "",
        });
        setTemplateDropdownOpen(false);
    };

    return (
        <div className={cx('estimate-layout-step-6-container')}>
            <div className={cx('header')}>
                <TitleEditor
                    value={formData?.titleStep6}
                    name={""}
                    placeholder={""}
                    inputkey={""}
                    isViewMode={false}
                    onChange={(value) => {
                        handleUpdateData({ titleStep6: value });
                    }}
                    styleView={{
                        fontSize: "20px",
                        fontWeight: 700,
                    }}
                />

                <div className={cx('button-container')} ref={templateDropdownRef}>
                    <div className={cx("template-dropdown-wrapper")}>
                        <button
                            type="button"
                            className={cx("left-content-body-menu-item")}
                            onClick={handleUseTemplateClick}
                        >
                            Use Template
                        </button>
                        {templateDropdownOpen && (
                            <div className={cx("template-dropdown")}>
                                {templatesLoading ? (
                                    <div className={cx("template-dropdown-loading")}>
                                        Loading...
                                    </div>
                                ) : (
                                    <ul className={cx("template-dropdown-list")}>
                                        {estimateTemplateIntroduction.length === 0 ? (
                                            <li className={cx("template-dropdown-empty")}>No templates</li>
                                        ) : (
                                            estimateTemplateIntroduction.map((template) => (
                                                <li key={template.id}>
                                                    <button
                                                        type="button"
                                                        className={cx("template-dropdown-item")}
                                                        onClick={() => handleSelectTemplate(template)}
                                                    >
                                                        {template.estimateTemplate.templateName || "Unnamed template"}
                                                    </button>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={cx('content')}>
                <div className={cx('content-label')}>
                    <p className={cx('label-text')}>Terms and Conditions</p>
                </div>
                <FormInpuEditor
                    name={"termsNConditions"}
                    text={""}
                    placeholder={""}
                    inputkey={"termsNConditions"}
                    value={formData?.termsNConditions}
                    isViewMode={false}
                    errorMessage={""}
                    height="100%"
                    onChange={(value) => {
                        handleUpdateData({
                            termsNConditions: value
                        })
                    }}
                />
            </div>
        </div>
    );
};

export default EstimateLayoutStep6;
